import { EASE_OUT } from "../motion";

type LiveHistoryTransitionOptions = {
  stack: HTMLElement;
  content: HTMLElement;
  history: HTMLElement;
  viewport: { height: number; bottom: number };
  onFinish: () => void;
};

export function startLiveHistoryTransition({
  stack,
  content,
  history,
  viewport,
  onFinish,
}: LiveHistoryTransitionOptions) {
  let animation: Animation | undefined;
  let active = true;
  let extraHeight = 0;
  let duration = 620;
  let targetBottom = 0;
  const bottomGap = Math.max(
    0,
    history.scrollHeight - history.clientHeight - history.scrollTop
  );

  function stop(notify: boolean) {
    if (!active) return;
    active = false;
    const gap = Math.max(
      0,
      history.scrollHeight - history.clientHeight - history.scrollTop
    );
    if (animation) {
      animation.onfinish = null;
      animation.cancel();
    }
    content.style.removeProperty("--live-history-extra-height");
    history.scrollTop = Math.max(
      0,
      history.scrollHeight - history.clientHeight - gap
    );
    if (notify) onFinish();
  }

  function preserveViewportHeight() {
    const nextHeight = Math.max(
      0,
      extraHeight + viewport.height - history.clientHeight
    );
    if (nextHeight === extraHeight) return;
    extraHeight = nextHeight;
    content.style.setProperty(
      "--live-history-extra-height",
      `${extraHeight}px`
    );
    history.scrollTop = Math.max(
      0,
      history.scrollHeight - history.clientHeight - bottomGap
    );
  }

  function readTargetBottom() {
    return (
      stack.getBoundingClientRect().top +
      history.getBoundingClientRect().bottom -
      content.getBoundingClientRect().top
    );
  }

  function play(offset: number) {
    animation = content.animate(
      [
        { transform: `translate3d(0, ${offset}px, 0)` },
        { transform: "translate3d(0, 0, 0)" },
      ],
      {
        duration,
        easing: EASE_OUT,
        fill: "both",
      }
    );
    animation.onfinish = () => stop(true);
  }

  function sync() {
    if (!active || !animation) return;
    preserveViewportHeight();
    const nextBottom = readTargetBottom();
    if (Math.abs(nextBottom - targetBottom) < 0.5) return;
    const offset =
      content.getBoundingClientRect().top - stack.getBoundingClientRect().top;
    const elapsed =
      typeof animation.currentTime === "number" ? animation.currentTime : 0;
    duration = Math.max(0, duration - elapsed);
    animation.onfinish = null;
    animation.cancel();
    // Feed growth must move the destination without moving the current visual position.
    play(offset + targetBottom - nextBottom);
    targetBottom = nextBottom;
  }

  if (
    !content.animate ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    stop(true);
  } else {
    preserveViewportHeight();
    targetBottom = readTargetBottom();
    play(viewport.bottom - targetBottom);
  }

  return { sync, finish: () => stop(true), dispose: () => stop(false) };
}
